/**
 * BarcodeByType - Renders barcode based on type (A, B, C, D)
 *
 * Extracted from ContagensListView to keep it under 500 lines.
 * Also contains helper functions and TIPOS_CODIGO constants.
 */
import React from 'react';
import Barcode from 'react-barcode';
import { Barcode as BarcodeIcon, QrCode, ScanLine, Keyboard } from 'lucide-react';
import { CompactRealDataMatrix, padGTIN14 } from './RealDataMatrix';
import { ClickableBarcode } from './ClickableBarcode';

// Helper: Format date for GS1 (YYMMDD)
export const formatDateForGS1 = (dateStr: string): string => {
  if (!dateStr) return '000000';
  try {
    const date = new Date(dateStr);
    const yy = String(date.getFullYear()).slice(-2);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yy}${mm}${dd}`;
  } catch {
    return '000000';
  }
};

// Helper: Ensure GTIN of 13 digits - recalculates checksum when necessary
export const padGTIN = (gtin: string): string => {
  const cleaned = gtin.replace(/\D/g, '');

  if (cleaned.length === 13) return cleaned;

  if (cleaned.length === 14 && cleaned.startsWith('0')) {
    const withoutLeadingZero = cleaned.substring(1);
    const code12 = withoutLeadingZero.slice(0, 12);
    let oddSum = 0;
    let evenSum = 0;
    for (let i = 0; i < 12; i++) {
      const digit = parseInt(code12[i]);
      if (i % 2 === 0) oddSum += digit;
      else evenSum += digit;
    }
    const total = oddSum + evenSum * 3;
    const checkDigit = (10 - (total % 10)) % 10;
    return code12 + checkDigit;
  }

  const code12 = cleaned.slice(0, 12).padStart(12, '0');
  let oddSum = 0;
  let evenSum = 0;
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(code12[i]);
    if (i % 2 === 0) oddSum += digit;
    else evenSum += digit;
  }
  const total = oddSum + evenSum * 3;
  const checkDigit = (10 - (total % 10)) % 10;
  return code12 + checkDigit;
};

export type TipoCodigo = 'A' | 'B' | 'C' | 'D';

export interface TipoCodigoInfo {
  tipo: TipoCodigo;
  nome: string;
  leituras: number;
  descricao: string;
  cor: string;
  bgCor: string;
  icon: 'barcode' | 'qr' | 'scan' | 'keyboard';
}

export const TIPOS_CODIGO: Record<TipoCodigo, TipoCodigoInfo> = {
  A: { tipo: 'A', nome: '2 Leituras', leituras: 2, descricao: 'GTIN + Lote/Validade separados', cor: 'text-blue-800', bgCor: 'bg-blue-100', icon: 'scan' },
  B: { tipo: 'B', nome: 'GS1-128', leituras: 1, descricao: 'Código completo em 1 leitura', cor: 'text-green-800', bgCor: 'bg-green-100', icon: 'barcode' },
  C: { tipo: 'C', nome: 'DataMatrix', leituras: 1, descricao: '2D com todos os dados', cor: 'text-purple-800', bgCor: 'bg-purple-100', icon: 'qr' },
  D: { tipo: 'D', nome: 'Apenas GTIN', leituras: 1, descricao: 'Lote/Validade manual', cor: 'text-amber-800', bgCor: 'bg-amber-100', icon: 'keyboard' },
};

interface BarcodeByTypeProps {
  tipo: TipoCodigo;
  gtin: string;
  lote: string;
  validade: string;
  productName?: string;
}

export const BarcodeByType: React.FC<BarcodeByTypeProps> = ({ tipo, gtin, lote, validade, productName }) => {
  const gs1Date = formatDateForGS1(validade);
  const paddedGtin = padGTIN(gtin);
  const gtin14 = padGTIN14(gtin);

  switch (tipo) {
    case 'A':
      return (
        <div className="flex flex-col gap-2 items-center">
          <ClickableBarcode productName={productName}>
            <div className="bg-white p-2 rounded border border-gray-200">
              <Barcode value={paddedGtin} format="EAN13" width={1.2} height={35} displayValue={false} margin={0} />
              <div className="text-[9px] text-center text-gray-500 mt-1">(01){paddedGtin}</div>
            </div>
          </ClickableBarcode>
          <ClickableBarcode productName={productName}>
            <div className="bg-white p-2 rounded border border-gray-200">
              <Barcode value={`17${gs1Date}10${lote || 'N/A'}`} format="CODE128" width={1.0} height={28} displayValue={false} margin={0} />
              <div className="text-[9px] text-center text-gray-500 mt-1">(17){gs1Date}(10){lote || 'N/A'}</div>
            </div>
          </ClickableBarcode>
        </div>
      );

    case 'B': {
      const gs1Full = `01${gtin14}17${gs1Date}10${lote || 'N/A'}`;
      return (
        <ClickableBarcode productName={productName}>
          <div className="bg-white p-2 rounded border border-gray-200">
            <Barcode value={gs1Full} format="CODE128" width={0.9} height={45} displayValue={false} margin={0} />
            <div className="text-[8px] text-center text-gray-500 mt-1 max-w-[150px] truncate">
              (01){gtin14}(17){gs1Date}(10){lote || 'N/A'}
            </div>
          </div>
        </ClickableBarcode>
      );
    }

    case 'C': {
      const dataMatrixContent = `(01)${gtin14}(17)${gs1Date}(10)${lote || 'N/A'}`;
      return (
        <ClickableBarcode productName={productName}>
          <div className="bg-white p-2 rounded border border-gray-200 flex flex-col items-center">
            <CompactRealDataMatrix data={dataMatrixContent} size={100} />
            <div className="text-[8px] text-center text-gray-500 mt-1 max-w-[100px] truncate">DataMatrix GS1</div>
          </div>
        </ClickableBarcode>
      );
    }

    case 'D':
    default:
      return (
        <ClickableBarcode productName={productName}>
          <div className="bg-white p-2 rounded border border-gray-200">
            <Barcode value={paddedGtin} format="EAN13" width={1.5} height={50} displayValue={true} fontSize={10} margin={0} />
            <div className="text-[8px] text-center text-amber-600 mt-1">Lote/Val. manual</div>
          </div>
        </ClickableBarcode>
      );
  }
};

export const TipoCodigoIcon: React.FC<{ tipo: TipoCodigoInfo['icon'] }> = ({ tipo }) => {
  switch (tipo) {
    case 'barcode': return <BarcodeIcon className="w-3 h-3" />;
    case 'qr': return <QrCode className="w-3 h-3" />;
    case 'scan': return <ScanLine className="w-3 h-3" />;
    case 'keyboard': return <Keyboard className="w-3 h-3" />;
    default: return <BarcodeIcon className="w-3 h-3" />;
  }
};
